/**
 * PaymentCreditTypesAdmin.tsx — Admin > Payments > Credit Types.
 *
 * Legacy's Payment Credit Types (lib/admin/admin-credit-type-list.php): the
 * account's names for patient credits (a payment shows a credit's type by this
 * name), in the Lead Sources layout (see components/admin/AdminList.tsx):
 *   - Title, A–Z until the column is clicked (no drag to reorder since
 *     2026-10-07: pickers list them alphabetically); search; Show deleted;
 *   - "Add credit type" top right; a click on a live row opens it; Edit /
 *     Delete on a live row, Restore on a deleted one;
 *   - legacy's form: Title *.
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live type still opens, read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareText, useListSort } from '@/components/admin/useListSort';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { getErrorMessage } from '@/services/apiClient';
import {
  createCreditType,
  deleteCreditType,
  listCreditTypes,
  restoreCreditType,
  updateCreditType,
  type AdminCreditType,
} from '@/services/creditTypesAdmin.service';
import { toast } from '@/store/toastStore';

const COLUMNS: AdminListColumn[] = [{ key: 'name', header: 'Title', sortable: true }, ACTIONS_COLUMN];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

function typeName(t: AdminCreditType): string {
  return t.name.trim() || `Credit type #${t.id}`;
}

export function PaymentCreditTypesAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [types, setTypes] = useState<AdminCreditType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [editing, setEditing] = useState<AdminCreditType | 'new' | null>(null);
  const [deleting, setDeleting] = useState<AdminCreditType | null>(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setTypes(await listCreditTypes());
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load credit types'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const query = search.trim().toLowerCase();
  const rows = useMemo(
    () =>
      types
        .filter((t) => (showDeleted || t.active) && (query === '' || t.name.toLowerCase().includes(query)))
        .sort((a, b) => compareText(typeName(a), typeName(b)) || a.id - b.id),
    [types, showDeleted, query],
  );
  const sorting = useListSort(rows, { initialKey: 'name', compare: { name: (a, b) => compareText(typeName(a), typeName(b)) } });
  const pagination = useListPagination(sorting.sorted, { resetOn: [query, showDeleted] });

  async function handleDelete() {
    if (!deleting) return;
    const { id } = deleting;
    try {
      await deleteCreditType(id);
      setTypes((prev) => prev.map((t) => (t.id === id ? { ...t, active: false } : t)));
      toast.success('Credit type deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'The credit type could not be deleted.'));
    }
  }

  async function handleRestore(t: AdminCreditType) {
    try {
      await restoreCreditType(t.id);
      toast.success('Credit type restored');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'The credit type could not be restored.'));
    }
  }

  return (
    <AdminListPage
      title="Payment Credit Types"
      subtitle="The types a patient credit can be given as. Payments show a credit by its type."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
          Add credit type
        </Button>
      }
      toolbar={
        <AdminListToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search credit types..."
          searchLabel="Search credit types"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="credit-types-show-deleted"
        />
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading credit types…" />
      ) : rows.length === 0 ? (
        <AdminListEmpty
          title={query ? 'No credit types found' : 'No credit types yet'}
          hint={query ? 'Try adjusting your search.' : locked ? undefined : 'Add a credit type to get started.'}
        />
      ) : (
        <>
          <AdminListTable
            columns={locked ? LOCKED_COLUMNS : COLUMNS}
            aria-label="Payment credit types"
            sort={sorting.sort}
            onSortChange={sorting.onSortChange}
          >
            {pagination.pageItems.map((t) => {
              const deleted = !t.active;
              const name = typeName(t);
              return (
                <tr
                  key={t.id}
                  data-row-id={t.id}
                  className={adminRowClass({ deleted, clickable: !deleted })}
                  onClick={deleted ? undefined : () => setEditing(t)}
                >
                  <td className={ADMIN_TD_CLASS}>
                    <AdminRowName deleted={deleted} nameTestId="credit-type-name">
                      {name}
                    </AdminRowName>
                  </td>
                  {!locked && (
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowActions>
                        {deleted ? (
                          <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(t)} />
                        ) : (
                          <>
                            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setEditing(t)} />
                            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting(t)} />
                          </>
                        )}
                      </AdminRowActions>
                    </td>
                  )}
                </tr>
              );
            })}
          </AdminListTable>
          <PaginationFooter pagination={pagination} noun={['credit type', 'credit types']} />
        </>
      )}

      {editing && (
        <CreditTypeFormModal
          key={editing === 'new' ? 'new' : editing.id}
          creditType={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load(true);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={deleting ? `Delete "${typeName(deleting)}"?` : ''}
        message="Credits already given keep this type. You can restore it from Show deleted."
        confirmLabel="Delete credit type"
        variant="danger"
      />
    </AdminListPage>
  );
}

function CreditTypeFormModal({
  creditType,
  onClose,
  onSaved,
}: {
  creditType: AdminCreditType | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = creditType === null;
  const t = useTrackedForm(() => ({ name: creditType?.name ?? '' }), { tracking: !isNew });
  const [saving, setSaving] = useState(false);
  const missing = t.form.name.trim() === '';

  async function save() {
    if (missing) return;
    setSaving(true);
    try {
      if (creditType) await updateCreditType(creditType.id, t.form.name);
      else await createCreditType(t.form.name);
      toast.success(isNew ? 'Credit type created' : 'Credit type updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The credit type could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Credit Type' : 'Edit Credit Type'}
      isNew={isNew}
      dirty={isNew ? !missing : t.dirty}
      saving={saving}
      errors={missing ? ['Title is required'] : []}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <DialogField
        label="Title"
        htmlFor="credit-type-name"
        required
        state={missing ? 'error' : 'none'}
        labelHint={missing ? '— required' : undefined}
        badge={t.badge('name', 'Title')}
      >
        <Input
          id="credit-type-name"
          value={t.form.name}
          placeholder="e.g. Referral credit"
          maxLength={255}
          onChange={(e) => t.patch({ name: e.target.value })}
          className={fieldClasses.input(missing ? 'error' : 'none')}
        />
      </DialogField>
    </AdminFormModal>
  );
}
